import React, { useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import LinkIcon from '@atlaskit/icon/core/link';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { LoadableLazy } from '@confluence/loadable';

import { buildAtAGlanceSummaries } from '../../at-a-glance/buildAtAGlanceSummaries.ts';
import type { UseAtAGlanceResult } from '../../at-a-glance/useAtAGlance.ts';

const styles = cssMap({
	// The connections block is a column: the standalone "At a glance", "Related content" and
	// "People & Agents" cards with the "Links" card between the last two.
	connectionsColumn: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.300'),
		boxSizing: 'border-box',
	},
	card: {
		display: 'flex',
		flexDirection: 'column',
		borderColor: token('color.border'),
		borderStyle: 'solid',
		borderWidth: token('border.width'),
		borderRadius: token('radius.large'),
		overflow: 'hidden',
		backgroundColor: token('elevation.surface'),
		boxSizing: 'border-box',
	},
	linksGroup: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.150'),
	},
	linksHeader: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
	},
	// The total-links count badge, matching the per-section count badge in the section headers.
	countBadge: {
		display: 'flex',
		height: '16px',
		minWidth: '24px',
		minHeight: '16px',
		paddingBlock: token('space.0'),
		paddingInline: token('space.050'),
		justifyContent: 'center',
		alignItems: 'center',
		flexShrink: 0,
		borderRadius: token('radius.xsmall'),
		backgroundColor: token('color.background.accent.gray.subtler'),
		font: token('font.body.small'),
		color: token('color.text.accent.gray'),
		boxSizing: 'border-box',
	},
});

const i18n = defineMessages({
	links: {
		id: 'byline-connections.preview-panel.links-header',
		defaultMessage: 'Links',
		description:
			'Header at the top of the Teamwork Graph connections card, above the individual connection sections. The total link count is shown in a badge beside it.',
	},
	linksEmpty: {
		id: 'byline-connections.preview-panel.links-empty',
		defaultMessage:
			'There is no linked content for this page right now. Add a link to strengthen the Teamwork Graph and improve cross-tool visibility.',
		description:
			'Shown under the "Links" header of the Teamwork Graph connections panel, in place of the connection sections, when the page has no links to, from, or related to it.',
	},
});

import { AppearsInSection } from './AppearsInSection.tsx';
import { AtAGlanceSection } from './AtAGlanceSection.tsx';
import { LinksToSection } from './LinksToSection.tsx';
import { PeopleAndAgentsSection } from './PeopleAndAgentsSection.tsx';
import { SectionEmptyState } from './SectionEmptyState.tsx';
import { SyncedFromSection } from './SyncedFromSection.tsx';
import type { ConnectionSectionsData } from './useConnectionSectionsData.ts';
import type { SectionExpansionController, SectionKey } from './useSectionExpansion.ts';

// The rows of "Related content" carry the Smart Link hover card, which brings the flexible-card
// view and the AI summary blocks with it. This component ships in the byline's after-paint chunk
// on every view page, so that section is a lazy boundary and loads once the panel or the modal
// mounts it. Nothing stands in while its chunk loads: the section is absent while its graph
// query is in flight anyway.
const RelatedContentSection = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-RelatedContentSection" */ './RelatedContentSection.tsx'
			)
		).RelatedContentSection,
});

export type ConnectionSectionsProps = {
	connections: ConnectionSectionsData;
	/**
	 * The cc-smarts response, fetched once by the surface that owns the connections. Not
	 * fetched here: the panel and the modal both render this component at the same time, so a
	 * call in here is a second paid LLM request for an answer already on screen.
	 */
	atAGlance: UseAtAGlanceResult;
	onPreviewOpen?: () => void;
	onAskRovo?: (prompt?: string) => void;
	/**
	 * Opens Rovo chat from the "Chat with Rovo" button at the end of "At a glance". Both surfaces
	 * pass it when Rovo is enabled, and both seed it with every connection url.
	 * Separate from `onAskRovo` because that one is bound to the `rowAction` trigger and seeds a
	 * single row's url.
	 */
	onAtAGlanceAskRovo?: () => void;
	/**
	 * Lifts each section's expanded state out to a caller that outlives the sections.
	 * Only the modal passes it, so its sections survive the switch to Rovo and back; the
	 * panel and popup omit it and let each section's shell own its own state.
	 */
	sectionExpansion?: SectionExpansionController;
};

/**
 * The panel's and the modal's connection sections, in the order the design stacks
 * them. Both surfaces render this so the set and its spacing stay in step, and both are
 * handed the one `useConnectionSectionsData` result the panel collected, so each section
 * gets its slice without a fetch of its own. "People & Agents" reads its slice like the
 * rest, though that slice comes from AGG rather than the SmartCard client.
 *
 * This component fetches nothing. Two things read the cc-smarts answer — "At a glance"
 * renders the overview bullets, and the rows of "Related content" render the one-liner
 * written for their link — so the surface hands it in and this file only splits it.
 * Appears in and Links to stay in the request for the overview; their rows do not
 * render one-liners.
 *
 * "Related content" sits above the "Links" card in a card of its own, so it is neither in
 * the Links count nor in what decides the Links empty state. With no links in either
 * direction, the "Links" card gives way to `SectionEmptyState` under the same header, once
 * the paged slice has settled.
 *
 * `onPreviewOpen` and `onAskRovo` are forwarded to the smart-link sections ("Appears in",
 * "Links to", "Related content") the same way, so their rows offer the preview and
 * "Ask Rovo" actions on both surfaces. "Synced from" and "At a glance" get neither — they
 * are not smart-link rows. "At a glance" instead takes `onAtAGlanceAskRovo`, the
 * "Chat with Rovo" button at the end of its body.
 */
export const ConnectionSections = ({
	connections,
	atAGlance,
	onPreviewOpen,
	onAskRovo,
	onAtAGlanceAskRovo,
	sectionExpansion,
}: ConnectionSectionsProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const aiSummaries = useMemo(() => buildAtAGlanceSummaries(atAGlance.output), [atAGlance.output]);

	// Resolves the controller to the controlled props a section forwards to its shell.
	// Returns nothing when uncontrolled, so the shell keeps owning the state.
	const expansionOf = (section: SectionKey) =>
		sectionExpansion
			? {
					isExpanded: sectionExpansion.isExpanded(section),
					onToggleExpanded: () => sectionExpansion.toggle(section),
				}
			: {};

	const totalLinks =
		(connections.syncedFrom.connection ? 1 : 0) +
		connections.appearsIn.links.length +
		connections.linksTo.links.length;
	const hasMore = connections.appearsIn.hasNextPage;
	// The card is replaced by the empty state only once the one slice that could still fill it
	// has settled; "Links to" and "Synced from" are read synchronously, so they never wait. A
	// failed incoming query is not an empty graph, so the card stays for that too.
	const hasNoLinks =
		totalLinks === 0 && !connections.appearsIn.loading && !connections.appearsIn.error;

	return (
		<Box xcss={styles.connectionsColumn}>
			<AtAGlanceSection {...atAGlance} onAskRovo={onAtAGlanceAskRovo} />
			<RelatedContentSection
				{...connections.relatedContent}
				aiSummaries={aiSummaries}
				onPreviewOpen={onPreviewOpen}
				onAskRovo={onAskRovo}
				{...expansionOf('relatedContent')}
			/>
			<Box xcss={styles.linksGroup}>
				<Box xcss={styles.linksHeader}>
					<LinkIcon label="" color="currentColor" />
					<Text weight="semibold" color="color.text">
						{formatMessage(i18n.links)}
					</Text>
					{totalLinks ? (
						<span css={styles.countBadge}>
							{totalLinks}
							{hasMore ? '+' : null}
						</span>
					) : null}
				</Box>
				{hasNoLinks ? (
					<SectionEmptyState
						message={formatMessage(i18n.linksEmpty)}
						testId="teamwork-graph-preview-panel-links-empty"
					/>
				) : (
					<Box xcss={styles.card}>
						<SyncedFromSection {...connections.syncedFrom} {...expansionOf('syncedFrom')} />
						<AppearsInSection
							{...connections.appearsIn}
							onPreviewOpen={onPreviewOpen}
							onAskRovo={onAskRovo}
							{...expansionOf('appearsIn')}
						/>
						<LinksToSection
							{...connections.linksTo}
							onPreviewOpen={onPreviewOpen}
							onAskRovo={onAskRovo}
							{...expansionOf('linksTo')}
						/>
					</Box>
				)}
			</Box>
			<PeopleAndAgentsSection
				people={connections.people.peopleAndAgents}
				loading={connections.people.loading || connections.agentViewers.loading}
				hasError={Boolean(connections.people.error || connections.agentViewers.error)}
				{...expansionOf('people')}
			/>
		</Box>
	);
};
